import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export type VirtualWindow = {
    startIndex: number;
    endIndex: number;
    offsetY: number;
    totalHeight: number;
};

function sameRange(a: VirtualWindow, b: VirtualWindow): boolean {
    return (
        a.startIndex === b.startIndex &&
        a.endIndex === b.endIndex &&
        a.offsetY === b.offsetY &&
        a.totalHeight === b.totalHeight
    );
}

/**
 * Measured-height row windowing.
 * - Scroll updates are rAF-coalesced; React only re-renders when the index range changes.
 * - Visible rows report real heights so offsetY / totalHeight stay accurate (no stop-jump,
 *   no phantom empty space at the bottom).
 */
export function useVirtualWindow(options: {
    enabled: boolean;
    count: number;
    estimateSize: number;
    overscan?: number;
    anchorEl: HTMLElement | null;
}): VirtualWindow & { reportSizes: (sizes: Array<{ index: number; size: number }>) => void } {
    const { enabled, count, estimateSize, overscan = 10, anchorEl } = options;

    const sizesRef = useRef<Array<number | undefined>>([]);
    const measuredSumRef = useRef(0);
    const measuredCountRef = useRef(0);
    const scrollTopRef = useRef(0);
    const viewportRef = useRef(600);
    const rafRef = useRef(0);
    const measureRafRef = useRef(0);

    const getEstimate = useCallback((): number => {
        if (measuredCountRef.current > 0) {
            return measuredSumRef.current / measuredCountRef.current;
        }
        return estimateSize;
    }, [estimateSize]);

    const getSize = useCallback(
        (index: number): number => {
            const measured = sizesRef.current[index];
            return measured != null ? measured : getEstimate();
        },
        [getEstimate],
    );

    const getOffset = useCallback(
        (index: number): number => {
            let offset = 0;
            for (let i = 0; i < index; i++) {
                offset += getSize(i);
            }
            return offset;
        },
        [getSize],
    );

    const getTotalHeight = useCallback((): number => {
        if (count <= 0) {
            return 0;
        }
        let total = 0;
        for (let i = 0; i < count; i++) {
            total += getSize(i);
        }
        return total;
    }, [count, getSize]);

    const findStartIndex = useCallback(
        (scrollTop: number): number => {
            if (count <= 0) {
                return 0;
            }
            // Linear scan is fine for ≤ a few hundred rows; keeps offsets exact.
            let offset = 0;
            for (let i = 0; i < count; i++) {
                const size = getSize(i);
                if (offset + size > scrollTop) {
                    return i;
                }
                offset += size;
            }
            return Math.max(0, count - 1);
        },
        [count, getSize],
    );

    const computeWindow = useCallback((): VirtualWindow => {
        if (count <= 0) {
            return { startIndex: 0, endIndex: 0, offsetY: 0, totalHeight: 0 };
        }
        const scrollTop = scrollTopRef.current;
        const viewportHeight = viewportRef.current;
        const startIndex = Math.max(0, findStartIndex(scrollTop) - overscan);

        let endIndex = startIndex;
        let covered = 0;
        const target = viewportHeight + overscan * getEstimate() * 2;
        while (endIndex < count && covered < target) {
            covered += getSize(endIndex);
            endIndex += 1;
        }
        endIndex = Math.min(count, Math.max(endIndex, startIndex + 1));

        return {
            startIndex,
            endIndex,
            offsetY: getOffset(startIndex),
            totalHeight: getTotalHeight(),
        };
    }, [count, findStartIndex, getEstimate, getOffset, getSize, getTotalHeight, overscan]);

    const [range, setRange] = useState<VirtualWindow>(() => computeWindow());
    const rangeRef = useRef(range);

    useEffect(() => {
        rangeRef.current = range;
    }, [range]);

    // Reset measurement cache when the row set is replaced.
    useEffect(() => {
        sizesRef.current = new Array(count);
        measuredSumRef.current = 0;
        measuredCountRef.current = 0;
        const next = computeWindow();
        rangeRef.current = next;
        setRange(next);
    }, [count, estimateSize]); // eslint-disable-line react-hooks/exhaustive-deps -- intentional reset on dataset change

    const publish = useCallback(() => {
        const next = computeWindow();
        if (sameRange(rangeRef.current, next)) {
            return;
        }
        rangeRef.current = next;
        setRange(next);
    }, [computeWindow]);

    useEffect(() => {
        if (!enabled || !anchorEl) {
            return;
        }
        const root =
            (anchorEl.closest('[data-table-scroll-root="true"]') as HTMLElement | null) ||
            (anchorEl.parentElement as HTMLElement | null);
        if (!root) {
            return;
        }

        const onScroll = (): void => {
            if (rafRef.current) {
                return;
            }
            rafRef.current = requestAnimationFrame(() => {
                rafRef.current = 0;
                scrollTopRef.current = root.scrollTop;
                viewportRef.current = root.clientHeight || 600;
                publish();
            });
        };

        scrollTopRef.current = root.scrollTop;
        viewportRef.current = root.clientHeight || 600;
        publish();

        root.addEventListener('scroll', onScroll, { passive: true });
        const ro =
            typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(() => {
                      scrollTopRef.current = root.scrollTop;
                      viewportRef.current = root.clientHeight || 600;
                      publish();
                  })
                : undefined;
        ro?.observe(root);
        return () => {
            root.removeEventListener('scroll', onScroll);
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = 0;
            }
            ro?.disconnect();
        };
    }, [enabled, anchorEl, publish]);

    const reportSizes = useCallback(
        (entries: Array<{ index: number; size: number }>) => {
            if (!enabled || entries.length === 0) {
                return;
            }
            let changed = false;
            for (const { index, size } of entries) {
                if (index < 0 || index >= count || !(size > 0)) {
                    continue;
                }
                const rounded = Math.round(size);
                const prev = sizesRef.current[index];
                if (prev != null && Math.abs(prev - rounded) < 1) {
                    continue;
                }
                if (prev == null) {
                    measuredSumRef.current += rounded;
                    measuredCountRef.current += 1;
                } else {
                    measuredSumRef.current += rounded - prev;
                }
                sizesRef.current[index] = rounded;
                changed = true;
            }
            if (!changed) {
                return;
            }
            if (measureRafRef.current) {
                return;
            }
            measureRafRef.current = requestAnimationFrame(() => {
                measureRafRef.current = 0;
                publish();
            });
        },
        [count, enabled, publish],
    );

    useEffect(() => {
        return () => {
            if (measureRafRef.current) {
                cancelAnimationFrame(measureRafRef.current);
            }
        };
    }, []);

    if (!enabled) {
        return {
            startIndex: 0,
            endIndex: count,
            offsetY: 0,
            totalHeight: count * estimateSize,
            reportSizes: () => undefined,
        };
    }

    return { ...range, reportSizes };
}

/** Measure `[data-virtual-index]` children after paint and report heights. */
export function useReportVirtualRowSizes(
    enabled: boolean,
    containerEl: HTMLElement | null,
    startIndex: number,
    endIndex: number,
    reportSizes: (sizes: Array<{ index: number; size: number }>) => void,
): void {
    useLayoutEffect(() => {
        if (!enabled || !containerEl) {
            return;
        }
        const nodes = containerEl.querySelectorAll<HTMLElement>('[data-virtual-index]');
        if (nodes.length === 0) {
            return;
        }
        const entries: Array<{ index: number; size: number }> = [];
        nodes.forEach(node => {
            const raw = node.getAttribute('data-virtual-index');
            if (raw == null) {
                return;
            }
            const index = Number(raw);
            if (!Number.isFinite(index)) {
                return;
            }
            entries.push({ index, size: node.getBoundingClientRect().height });
        });
        reportSizes(entries);
    }, [enabled, containerEl, startIndex, endIndex, reportSizes]);
}
